import { InputWithLabel } from '@adotel/components'
import { Button, CircularProgress } from '@material-ui/core'

import { MAX_PROMOTION_CODE_LENGTH } from '../../../../helpers/promotion'
import { getTranslate } from '../../../../localization'
import useStyles from './styles'

/**
 * Id of the code input.
 *
 * Exported because focus recovery has to find this element by id rather than by ref. At the
 * pinned @adotel/components 1.1.9, InputWithLabel DECLARES `React.RefAttributes<HTMLInputElement>`
 * and forwards the ref to its wrapper DIV — verified by probe, `ref.current.tagName === "DIV"`.
 * A div is not focusable, so ref-based focusing is a silent no-op. Same shape as the
 * htmlFor/id defect documented below: the library's type says one thing and its runtime does
 * another, and both fail without an error.
 */
export const CODE_FIELD_ID = 'promotion-code'

type CodeFieldProps = {
  code: string
  disabled: boolean
  checking: boolean
  helperText: string
  error: boolean
  /**
   * Id of the element describing the current error, or undefined when there is none.
   *
   * The refusal lives outside this component, so the association has to be passed in. The
   * component never had one: aria-describedby appears nowhere in @adotel/components' runtime
   * bundle, so `helpertext` was visible-only all along.
   */
  messageId?: string
  onChange: (value: string) => void
  onApply: () => void
}

/**
 * The code field and the action that checks it.
 *
 * `InputWithLabel` rather than a raw MUI `TextField`, and the reason is local rather than
 * borrowed from another repo's conventions: the booker and guest forms in THIS booking
 * stepper already use it, so a guest typed their name into one component and a promotion
 * code into another, on the same page.
 *
 * BOTH `id` and `name`, deliberately, and this is the part worth reading before copying
 * this component. At the pinned `@adotel/components` 1.1.9, `InputWithLabel` renders
 * `<label htmlFor={name}>` while passing `name` to the input and letting `id` arrive only
 * through the prop spread. `<label for>` matches an element's **id**, never its name — so
 * the association holds only when a caller passes an `id` and a `name` with the same
 * value. Pass one without the other and the field has NO accessible name while still
 * looking correct.
 *
 * The library's own 1.3.x fix records the scale of that: "Measured across admin-panel on
 * 2026-08-14: 228 of 324 call sites rendered a visible label with no accessible name." It
 * resolves `id` from `name` via `useFieldId`, after which passing either alone is enough.
 * Until this repo is on that version, pass both.
 *
 * `getByLabelText` in the test suite is what enforces this — it resolves only on a real
 * association. An earlier attempt here passed `name` alone and failed with "no form
 * control was found associated to that label", which is how the requirement was found.
 *
 * The 128-cap moved into `onChange` because `InputWithLabel` exposes no `inputProps`. It
 * is NOT equivalent to the native `maxlength` it replaces: native `maxlength` is
 * composition-aware, deferring truncation to `compositionend` and never touching an
 * in-progress composed string, whereas re-setting a controlled value every keystroke can
 * desync an active IME. Acceptable here because a promotion code is short ASCII and the
 * truncation only bites at the maximum, which no composition reaches. On a free-text field
 * it would matter — do not lift the pattern verbatim.
 *
 * The cap here is an editing convenience, not the control. `buildPromotionIntent` enforces
 * the same bound on Apply, and that is the check that decides.
 *
 * The button is CONTAINED primary. It was outlined, which reads as secondary next to a
 * field it is the only action for.
 */
const CodeField = ({
  code,
  disabled,
  checking,
  helperText,
  error,
  messageId,
  onChange,
  onApply
}: CodeFieldProps) => {
  const classes = useStyles()

  return (
    <div className={classes.codeFieldRow}>
      <InputWithLabel
        // The id is load-bearing for THREE things now: the label association (see above),
        // aria-describedby, and focus recovery after the applied-offer swap. It cannot be
        // dropped or made dynamic without breaking one of them.
        id={CODE_FIELD_ID}
        name={CODE_FIELD_ID}
        autoComplete="off"
        className={classes.codeField}
        disabled={disabled}
        error={error}
        // aria-invalid explicitly rather than trusting `error` to reach the input. MUI's own
        // InputBase derives it, but `error` is passed to a WRAPPER here and whether that
        // forwards it or only styles with it is not verifiable from this repo — so it is
        // stated rather than assumed.
        aria-invalid={error}
        aria-describedby={messageId}
        fullWidth
        helpertext={helperText}
        inputSize="small"
        label={getTranslate('Promotion code (optional)')}
        spellCheck={false}
        value={code}
        // Submitting from the keyboard is the natural gesture in a one-field form; without
        // it the only way to check a code is to leave the field for the button.
        onKeyDown={(event) => {
          if (
            event.key === 'Enter' &&
            !disabled &&
            !checking &&
            code.trim() !== ''
          ) {
            onApply()
          }
        }}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          onChange(
            event.currentTarget.value.slice(0, MAX_PROMOTION_CODE_LENGTH)
          )
        }
      />
      <Button
        className={classes.applyButton}
        color="primary"
        variant="contained"
        // Explicit, not debounced or on-change. A code is an assertion the booker makes,
        // and checking half-typed input reports failures for codes nobody entered.
        disabled={disabled || checking || code.trim() === ''}
        // The label stays put while the spinner replaces the visible text; without it the
        // accessible name disappears at exactly the moment the user wants to know what is
        // happening. aria-busy says which moment that is.
        aria-label={getTranslate('Apply')}
        aria-busy={checking}
        onClick={onApply}>
        {checking ? <CircularProgress size={16} /> : getTranslate('Apply')}
      </Button>
    </div>
  )
}

export default CodeField
